import React from 'react';
import PropTypes from 'prop-types';
import autosize from 'autosize';
import stringUtils from './../../../../../qti/utils/StringUtils';
import { t } from '../../../../../services/i18nService';
import scorableItemNumberingService from '../../../../../services/questionView/ScorableItemNumberingService';

import styles from './plainTextWrapper.scss';
import TextCounter from './TextCounter/TextCounter';

const ESTIMATED_NORMAL_FONT_LINE_HEIGHT_PLAINTEXT = 19; // approximation
const DEFAULT_NUMBER_OF_LINES_PLAINTEXT = 9;

class ExtendedTextPlainText extends React.Component {
    static propTypes = {
        autoExpand: PropTypes.bool,
        autoSave: PropTypes.func.isRequired,
        contentRevisionId: PropTypes.number.isRequired,
        expectedLines: PropTypes.number,
        fieldWidth: PropTypes.string,
        handleInteractionReady: PropTypes.func.isRequired,
        identifier: PropTypes.string.isRequired,
        isReadOnly: PropTypes.bool,
        maxWords: PropTypes.number,
        shouldSaveOnEmptyChange: PropTypes.bool.isRequired,
        showWordCount: PropTypes.bool,
        userData: PropTypes.string, // The previously stored data
    };

    constructor(props) {
        super(props);
        this.maxChars = parseInt(window.marketplaceProperties.ESSAY_MAX_CHARS, 10) || -1;
        this.maxWords = this.props.maxWords || 0;

        this.state = {
            isDirty: false,
            isEmpty: !this.props.userData || this.props.userData.length === 0,
            text: this.props.userData,
        };
    }

    componentDidMount() {
        this.props.handleInteractionReady(true);
        if (this.props.autoExpand) {
            autosize(this.textarea);
        }
    }

    handleTextChanged = (event) => {
        const { autoSave, shouldSaveOnEmptyChange } = this.props;
        const value = event.target.value;

        let textAreaWords = stringUtils.countWords(value);
        const isMaxCharReached = this.reachedMaxCharsLimit(value);

        let text;
        if (this.reachedMaxWordsLimit(textAreaWords) || isMaxCharReached) {
            text = stringUtils.whitespaceSubstring(this.state.text, this.maxChars);
            textAreaWords = stringUtils.countWords(text);

            if (this.pasted) {
                let alertMessage = t('essay.exceedingMaxChars');
                if (isMaxCharReached) {
                    alertMessage += `.\n${t('essay.maxChars', { limit: this.maxChars })}`;
                }
                alert(alertMessage);
            }
        } else {
            text = value;
        }

        this.setState({
            isDirty: true,
            text,
        });

        this.pasted = false;

        if (shouldSaveOnEmptyChange) {
            const isEditorEmpty = value.length === 0;
            if (this.state.isEmpty !== isEditorEmpty) {
                this.setState({
                    isEmpty: isEditorEmpty,
                });
                // timeout is needed here because need to be sure that isDirty state value has changed
                // timeout can be avoided by using functional component approach
                setTimeout(autoSave, 100);
            }
        }
    };

    handleTextPasted = () => {
        this.pasted = true;
    }

    reachedMaxCharsLimit = text => (this.maxChars > 0 && stringUtils.countChars(text) > this.maxChars);

    reachedMaxWordsLimit = text => (this.maxWords > 0 && text > this.maxWords);

    setAutoSize = () => {
        const containerEl = this.textarea;
        const initialNumberOfLines = this.props.expectedLines || DEFAULT_NUMBER_OF_LINES_PLAINTEXT;
        const height = `${(initialNumberOfLines * ESTIMATED_NORMAL_FONT_LINE_HEIGHT_PLAINTEXT) + 24}px`;

        if (this.props.autoExpand) {
            containerEl.style.minHeight = height;
        } else {
            containerEl.style.height = height;
        }
    };

    /* BEGIN functions called by parent */
    getEditorData = () => {
        this.setState({ isDirty: false });
        return this.state.text;
    };

    resetEditor = () => {
        if (this.isEditorInitialized()) {
            this.setState({
                isDirty: true,
                text: '',
            });
        }
    };

    isDirty = () => (this.state.isDirty);

    isEditorInitialized = () => (!!this.textarea);
    /* END functions called by parent */

    render() {
        const { isReadOnly = false, expectedLines, contentRevisionId, identifier } = this.props;
        const inlineStyle = {};

        // Custom support for setting field width on text area question type
        if (this.props.fieldWidth) {
            inlineStyle.width = this.props.fieldWidth;
        }
        const interactionNumber = scorableItemNumberingService.getInteractionNumber(contentRevisionId, identifier);
        let scorableItemId;
        if (interactionNumber) {
            scorableItemId = `scorableItem-${contentRevisionId}_${interactionNumber}`;
        }

        return (
            <div id={scorableItemId}>
                <textarea
                    aria-label={t('interactions.extendedTextPlain.editorWrapperAriaLabel')}
                    className={styles.plainText}
                    style={inlineStyle}
                    value={this.state.text}
                    rows={expectedLines || 1}
                    disabled={isReadOnly}
                    onChange={this.props.isReadOnly ? null : this.handleTextChanged}
                    onPaste={this.props.isReadOnly ? null : this.handleTextPasted}
                    spellCheck="false"
                    ref={(input) => {
                        this.textarea = input;
                    }}
                />

                <TextCounter
                    text={this.state.text}
                    showWordCount={this.props.showWordCount}
                    maxWords={this.maxWords}
                    maxChars={this.maxChars}
                />
            </div>
        );
    }
}

export default ExtendedTextPlainText;
